import { useEffect, useState } from 'react';

// A load that settles faster than this never flashes the wheel.
export const LOADING_SPINNER_DELAY_MS = 180;

/** True once `active` has held for `delayMs`; false again the moment it drops. */
export function useDelayedFlag(active: boolean, delayMs: number = LOADING_SPINNER_DELAY_MS): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (!active) {
      setElapsed(false);
      return;
    }
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
  return active && elapsed;
}

/** Mount while a load is pending: an empty, layout-holding slot until the delay
 * passes, then the wheel. `overlay` floats it over content that stays on screen. */
export function LoadingSpinner({ label, overlay = false }: { label: string; overlay?: boolean }) {
  const visible = useDelayedFlag(true);
  const status = visible ? (
    <div className="loading-spinner" role="status" aria-live="polite">
      <svg className="loading-wheel" viewBox="0 0 40 40" width="40" height="40" aria-hidden>
        <circle className="loading-wheel-track" cx="20" cy="20" r="17" />
        <circle className="loading-wheel-arc" cx="20" cy="20" r="17" />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  ) : null;
  if (overlay) return status && <div className="loading-overlay">{status}</div>;
  return <div className="loading-slot">{status}</div>;
}
